import type { Theme, StyleRules } from "@material-ui/core";
import { darken } from "@material-ui/core";
// eslint-disable-next-line
import type { Overrides } from "@material-ui/core/styles/overrides";

export const getComponentOverrides = (baseTheme: Theme) => {
    const overrides: Overrides = {
        MuiAvatar: {
            colorDefault: {
                backgroundColor: baseTheme.palette.grey[600],
                color: "#fff",
            },
            root: {
                boxSizing: "border-box",
                "&.primary": {
                    backgroundColor: baseTheme.palette.grey[800],
                    color: "#fff",
                },
                "&.disabled": {
                    backgroundColor: baseTheme.palette.grey[300],
                    color: "#fff",
                },

                "&.inline": {
                    display: "inline-flex",
                    fontSize: "1rem",
                },
                "&.MuiAvatar-sizeSmall": {
                    height: 22,
                    width: 22,
                },
                "&.error": {
                    backgroundColor: "#e51c23",
                    color: "#fff",
                },
                "&.warning": {
                    backgroundColor: "#ff9800",
                    color: "#fff",
                },
                "&.info": {
                    backgroundColor: "#f2df00",
                    color: "#4a4a4a",
                },
            },
        },
        MuiButton: {
            root: {
                fontWeight: 400,
            },
            containedPrimary: {
                "&:hover": {
                    backgroundColor: darken(baseTheme.palette.primary.main, 0.2),
                },
            },
            contained: {
                backgroundColor: "#fff",
            },
        },
        MuiCheckbox: {
            root: {
                // Override bootstrap styles
                "& .MuiIconButton-label input[type=checkbox]": {
                    position: "absolute",
                    padding: 0,
                    margin: 0,
                },
            },
        },
        MuiChip: {
            root: {
                backgroundColor: "rgba(0, 0, 0, 0.08)",
                fontSize: "1.2rem",
                "&~&": {
                    marginLeft: baseTheme.spacing(0.5),
                },
            },

            outlined: {
                borderColor: "rgba(0, 0, 0, 0.12)",
            },
        },
        MuiDrawer: {
            paper: {
                backgroundColor: "#f2f2f2",
            },
        },
        MuiInput: {
            underline: {
                "&:before": {
                    borderBottomColor: baseTheme.palette.grey[400],
                },
                "&:hover:not(.Mui-disabled):before": {
                    borderBottomColor: baseTheme.palette.grey[600],
                    borderBottomWidth: 1,
                },
            },
        },
        MuiInputBase: {
            input: {
                // Bootstrap override
                "input&": {
                    boxShadow: "none",
                    padding: "6px 0 7px",
                },
            },
        },
        MuiListItem: {
            root: {
                paddingBottom: baseTheme.spacing(1),
                paddingTop: baseTheme.spacing(1),
                "&.MuiListItem-noPadding": {
                    paddingBottom: 0,
                    paddingTop: 0,
                },
                "& a, & a:hover": {
                    color: "inherit",
                },
            },
        },
        MuiListItemIcon: {
            root: {
                minWidth: baseTheme.spacing(5),
            },
        },
        MuiOutlinedInput: {
            input: {
                // Bootstrap override
                "input&": {
                    padding: "18.5px 14px",
                },
            },
            inputAdornedEnd: {
                // Bootstrap override
                "input&": {
                    paddingRight: 0,
                },
            },
            inputAdornedStart: {
                // Bootstrap override
                "input&": {
                    paddingLeft: 0,
                },
            },
            inputMarginDense: {
                // Bootstrap override
                "input&": {
                    paddingTop: 10.5,
                    paddingBottom: 10.5,
                },
            },
            notchedOutline: {
                // Bootstrap override
                "& legend": {
                    marginLeft: 0,
                },
            },
        },
        MuiSwitch: {
            /* Override bootstrap */
            input: {
                "&[type=checkbox]": {
                    position: "absolute!important",
                },
            },
        },
        MuiTableCell: {
            root: {
                padding: baseTheme.spacing(1.5),
                "& a, & a:hover": {
                    color: "inherit",
                },
            },
            head: {
                ...baseTheme.typography.overline,
                color: baseTheme.palette.text.secondary,
                paddingBottom: baseTheme.spacing(0.5),
                paddingTop: baseTheme.spacing(0.5),
            },
            body: {
                borderBottomColor: "#f2f2f2",
                // Hack for material-table actions column
                "&.MuiTableCell-paddingNone > div > .MuiButtonBase-root": {
                    paddingBottom: baseTheme.spacing(1.25),
                    paddingLeft: baseTheme.spacing(1),
                    paddingRight: baseTheme.spacing(1),
                    paddingTop: baseTheme.spacing(1.25),
                },
            },
        },
        MuiTablePagination: {
            toolbar: {
                color: baseTheme.palette.text.secondary,
            },
        },
        MuiTableSortLabel: {
            root: {
                marginRight: "-20px",
                // ...baseTheme.typography.overline,
            },
            icon: {
                marginLeft: "2px",
            },
        },
        MuiTabs: {
            root: {
                borderBottom: "1px solid grey",
                borderBottomColor: baseTheme.palette.grey[300],
                marginBottom: baseTheme.spacing(2),
            },
        },
        MuiTextField: {
            root: {},
        },
        MuiToolbar: {
            gutters: {
                // Hack for material-table
                paddingLeft: baseTheme.spacing(1.5),
                paddingRight: baseTheme.spacing(1.5),
                [baseTheme.breakpoints.up("sm")]: {
                    paddingLeft: baseTheme.spacing(1.5),
                    paddingRight: baseTheme.spacing(1.5),
                },
            },
        },
    };

    // Missing typings
    (overrides as any).MuiAvatarGroup = {
        root: {
            "&.MuiAvatarGroup-inline": {
                display: "inline-flex",
                marginLeft: -1,
                position: "relative",

                "& .MuiAvatar-root": {
                    fontSize: "1.3rem",
                    height: 32,
                    top: 1,
                    width: 32,
                },
            },
            "&.MuiAvatarGroup-sizeSmall, &.MuiAvatarGroup-inline.MuiAvatarGroup-sizeSmall": {
                height: 24,
                "& .MuiAvatar-root": {
                    fontSize: "1rem",
                    height: 26,
                    top: -1,
                    width: 26,
                },
            },
        },
    } as Partial<StyleRules<any>>;

    return overrides;
};
